import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateOnly, dateTime, money } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Alert, Button } from '@/components/ui/primitives';
import { BatchStatusDialog } from './BatchStatusDialog';
import { RecallReportCard } from './RecallReportCard';
import { Pager, StockStatusBadge, formatQty } from './stock-control-common';
import { PAGE_SIZE, type BlockedBatch } from './stock-control-types';

export function BatchControlPanel() {
  const { can } = useAuth();
  const [offset, setOffset] = useState(0);
  const [releasing, setReleasing] = useState<BlockedBatch | null>(null);

  const mayManage = can('stock.batch.manage');
  const mayRead = can('stock.read');
  const mayReport = can('stock.batch.manage', 'pharmacy.read');

  const blocked = useQuery({
    queryKey: ['stock-control-blocked', offset],
    queryFn: () => get<BlockedBatch[]>(`/pharmacy/stock/batches/blocked${qs({ limit: PAGE_SIZE, offset })}`),
    placeholderData: keepPreviousData,
    enabled: mayRead,
  });
  const rowCount = blocked.data?.length ?? 0;
  const paged = offset > 0 || rowCount >= PAGE_SIZE;

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold">{tr('pharmacy', 'quarantined_and_recalled_stock_on_the_shelf')}</h2>
          <p className="text-sm text-muted-foreground">
            {tr('pharmacy', 'blocked_batches_cannot_be_sold_issued_or')}
          </p>
        </div>

        {mayRead ? (
          <DataTable
            rows={blocked.data}
            loading={blocked.isLoading} refreshing={blocked.isFetching}
            error={blocked.error}
            rowKey={(row) => row.id}
            empty={tr('pharmacy', 'nothing_is_quarantined_or_recalled_blocked')}
            footer={
              paged ? (
                <div className="w-full">
                  <Pager offset={offset} pageSize={PAGE_SIZE} rowCount={rowCount} disabled={blocked.isFetching} onOffsetChange={setOffset} />
                </div>
              ) : undefined
            }
            columns={[
              {
                header: tr('pharmacy', 'product'),
                sort: (row) => row.product.genericName,
                cell: (row) => (
                  <div>
                    <p className="font-medium">{`${row.product.genericName} ${row.product.strength ?? ''}`.trim()}</p>
                    <p className="text-xs text-muted-foreground">{row.product.code}</p>
                  </div>
                ),
              },
              { header: tr('pharmacy', 'store'), sort: (row) => row.store.name, cell: (row) => row.store.name },
              { header: tr('pharmacy', 'batch'), sort: (row) => row.batchNo, cell: (row) => <span className="font-mono text-xs">{row.batchNo}</span> },
              { header: tr('pharmacy', 'expires'), sort: (row) => row.expiryDate, cell: (row) => dateOnly(row.expiryDate) },
              { header: tr('pharmacy', 'on_hand'), align: 'right', sort: (row) => row.qtyOnHand, cell: (row) => formatQty(row.qtyOnHand) },
              { header: tr('pharmacy', 'value_at_cost'), align: 'right', sort: (row) => row.valueMinor, cell: (row) => money(row.valueMinor) },
              {
                header: tr('pharmacy', 'status'),
                sort: (row) => row.status,
                cell: (row) => (
                  <div>
                    <StockStatusBadge status={row.status} />
                    {row.statusReason && <p className="mt-0.5 text-xs text-muted-foreground">{row.statusReason}</p>}
                  </div>
                ),
              },
              { header: tr('pharmacy', 'since'), sort: (row) => row.statusChangedAt, cell: (row) => dateTime(row.statusChangedAt) },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  mayManage ? (
                    <Button size="sm" variant="outline" onClick={() => setReleasing(row)}>
                      {tr('pharmacy', 'release')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        ) : (
          <Alert>{tr('pharmacy', 'seeing_the_list_of_blocked_stock_needs_the')}</Alert>
        )}
      </section>

      {mayReport && <RecallReportCard />}

      {releasing && <BatchStatusDialog preset={releasing} onClose={() => setReleasing(null)} />}
    </div>
  );
}
